import React from 'react';

import { useParams } from 'react-router-dom';

import { PrimaryButton, Tabs, Tab, colors } from '@crehana/ui';

import { Message } from '@crehana/icons';

import usePusherChannel from '@crehana/web/dist/ReactContexts/PusherContext/hooks/usePusherChannel';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

import { useMe } from 'Jsx/ContextApis/AuthContext';

import {
  LiveNode,
  LiveNode_moderators_edges as ModeratorEdges,
} from 'Jsx/CrehanaLive/graphql/types/LiveNode';
import useChatActions from 'Jsx/CrehanaLive/hooks/useChatActions';
import useLiveQueryInbox from 'Jsx/CrehanaLive/hooks/useLiveInboxQuery';

import { ChatNode } from 'Jsx/CrehanaLive/graphql/types/ChatNode';

import Information from './Information';
import Chat from './Chat';
import Questions from './Questions';

interface LiveTabsProps {
  live: LiveNode;
  liveIsActive?: boolean;
  chatIsActive?: boolean;
  canSendMessages?: boolean;
  isFuture?: boolean;
  isPoppedOut?: boolean;
  closePopup?: () => void;
  enrollToLive?: (liveId: number) => Promise<void>;
  isForbiddenPremiumLive?: boolean;
}

enum LIVE_TABS {
  CHAT = 0,
  QUESTIONS = 1,
  INFORMATION = 2,
}

const LiveTabs: React.FC<LiveTabsProps> = ({
  live,
  liveIsActive,
  chatIsActive,
  canSendMessages,
  isFuture,
  isPoppedOut,
  closePopup,
  enrollToLive,
  isForbiddenPremiumLive,
}) => {
  const { t } = useCrehanaLiveTranslations();
  const { me: user, loading: loadingUser } = useMe();
  const reactRouterParams = useParams<{ liveSlug: string; orgSlug?: string }>();
  const [tabSelected, setTabSelected] = React.useState(
    live.isLive ? LIVE_TABS.CHAT : LIVE_TABS.INFORMATION,
  );
  const [tabsUnseen, setTabsUnseen] = React.useState({
    currentTab: tabSelected,
    unseen: { messages: 0, questions: 0 },
  });
  const isLoggedIn = Boolean(!loadingUser && user);

  React.useEffect(() => {
    if (liveIsActive) {
      setTabSelected(LIVE_TABS.CHAT);
      setTabsUnseen(prev => ({
        currentTab: LIVE_TABS.CHAT,
        unseen: { ...prev.unseen, messages: 0 },
      }));
    }
  }, [liveIsActive]);

  const chatActions = useChatActions(live);

  const onTabChange = (value: LIVE_TABS) => {
    setTabSelected(value);
    setTabsUnseen(prev => ({
      currentTab: value,
      unseen: {
        messages: value === LIVE_TABS.CHAT ? 0 : prev.unseen.messages,
        questions: value === LIVE_TABS.QUESTIONS ? 0 : prev.unseen.questions,
      },
    }));
  };

  const hasModeratorPermission = React.useMemo(() => {
    if (!user) {
      return false;
    }
    const userId = user.originalId;
    // The type for moderators and presenters is the same (UserNode)
    const search = (edge: ModeratorEdges | null) =>
      edge!.node!.originalId === userId;

    const isMod = Boolean(live.moderators?.edges.find(search));

    if (isMod) {
      return true;
    }

    // Presenters also have moderator permissions
    const isPresenter =
      live.presenter?.originalId === user.originalId ||
      Boolean(live.presenters?.edges.find(search));

    if (isPresenter) {
      return true;
    }

    return false;
  }, [live.moderators, live.presenter, live.presenters, user]);

  const {
    error: inboxError,
    loading: loadingInbox,
    refetch: refetchInboxData,
    messages,
    chatPinned,
    questions,
    onReceiveMessage,
    onHideMessage,
    onLikeMessage,
    onMoveQuestion,
    onPinMessage,
    onUnpinMessage,
  } = useLiveQueryInbox(
    { slug: reactRouterParams.liveSlug, orgSlug: reactRouterParams.orgSlug },
    { isB2B: !!reactRouterParams.orgSlug },
  );

  const onMessage = (data: ChatNode) => {
    setTabsUnseen(prev => ({
      ...prev,
      unseen: {
        ...prev.unseen,
        messages:
          prev.currentTab !== LIVE_TABS.CHAT ? prev.unseen.messages + 1 : 0,
      },
    }));
    onReceiveMessage(data);
  };

  const onMoveQuestionAction = (data: ChatNode) => {
    setTabsUnseen(prev => ({
      ...prev,
      unseen: {
        ...prev.unseen,
        questions:
          prev.currentTab !== LIVE_TABS.QUESTIONS
            ? prev.unseen.questions + 1
            : 0,
      },
    }));
    onMoveQuestion(data);
  };

  const {
    connected: isPusherConnected,
    subscriptionSucceeded: isPusherSubscriptionSucceeded,
  } = usePusherChannel({
    channelName: live.channel!,
    events: [
      { eventName: live.channelEvent!, handler: onMessage },
      { eventName: live.channelEventAddPin!, handler: onPinMessage },
      { eventName: live.channelEventRemovePin!, handler: onUnpinMessage },
      { eventName: live.channelEventHideChat!, handler: onHideMessage },
      {
        eventName: live.channelEventChatQuestion!,
        handler: onMoveQuestionAction,
      },
      { eventName: live.channelEventLikeChat!, handler: onLikeMessage },
    ],
  });

  const getUnseenMessagesLabel = (total: number) => {
    return total > 99 ? '+99' : total;
  };

  if (isPoppedOut) {
    return (
      <div className="relative flex-1 bg-base-lighter-16 md:h-screen">
        <div className="h-full w-full flex flex-1 flex-col items-center justify-center">
          <Message size={48} color={colors.baseLighter} className="mb-16" />
          <p className="font-h4 text-white mb-24">
            {t('HIDDEN_CHAT_LIVE_LABEL')}
          </p>
          <PrimaryButton
            darkMode
            label={t('SHOW_CHAT_LIVE_LABEL')}
            onClick={closePopup}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <Tabs
          onTabChange={onTabChange}
          value={tabSelected}
          backgroundColor="base-lighter-16"
          isDark
          size="s"
        >
          {chatIsActive && !isForbiddenPremiumLive && (
            <Tab
              label={
                tabsUnseen.unseen.messages
                  ? `${t('CHAT_LIVE_LABEL')} (${getUnseenMessagesLabel(
                      tabsUnseen.unseen.messages,
                    )})`
                  : t('CHAT_LIVE_LABEL')
              }
              value={LIVE_TABS.CHAT}
            />
          )}
          {chatIsActive && !isForbiddenPremiumLive && (
            <Tab
              label={
                tabsUnseen.unseen.questions
                  ? `${t('QUESTION_LIVE_LABEL')} (${getUnseenMessagesLabel(
                      tabsUnseen.unseen.questions,
                    )})`
                  : t('QUESTION_LIVE_LABEL')
              }
              value={LIVE_TABS.QUESTIONS}
            />
          )}
          <Tab
            label={t('ABOUT_TAB_LIVE_LABEL')}
            value={LIVE_TABS.INFORMATION}
          />
        </Tabs>
      </div>
      <div className="relative flex-1 bg-base-lighter-16 md:h-full">
        {!isForbiddenPremiumLive && tabSelected === LIVE_TABS.CHAT && (
          <Chat
            loadingUser={loadingUser}
            user={user || undefined}
            loadingInbox={loadingInbox}
            error={inboxError}
            messages={messages}
            chatPinned={chatPinned}
            isPusherConnected={isPusherConnected}
            isPusherSubscriptionSucceeded={isPusherSubscriptionSucceeded}
            refetchInboxData={refetchInboxData}
            hasModeratorPermission={hasModeratorPermission}
            isLoggedIn={isLoggedIn}
            canSendMessages={canSendMessages}
            {...chatActions}
          />
        )}
        {!isForbiddenPremiumLive && tabSelected === LIVE_TABS.QUESTIONS && (
          <Questions
            questions={questions}
            error={Boolean(inboxError)}
            refetch={refetchInboxData}
            hasModeratorPermission={hasModeratorPermission}
            isLoggedIn={isLoggedIn}
            goToChat={() => setTabSelected(LIVE_TABS.CHAT)}
            areActionsEnabled={canSendMessages}
            {...chatActions}
          />
        )}
        {(isForbiddenPremiumLive || tabSelected === LIVE_TABS.INFORMATION) && (
          <Information
            live={live}
            enrollToLive={enrollToLive}
            isFuture={isFuture}
            isForbiddenPremiumLive={isForbiddenPremiumLive}
          />
        )}
      </div>
    </>
  );
};

export default LiveTabs;
